import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { LineChart } from '@/components/charts/LineChart';
import { PHBarChart } from '@/components/charts/PHBarChart';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Droplets } from 'lucide-react';
import { cn } from '@/lib/cn';
import { checkToChartRow, slotClock } from '@/components/pond/page/waterChecks';
import type { ChartCheck } from '@/types/generated/openapi';

type TimeRange = '7d' | '30d' | 'cycle';

interface WaterQualityGridProps {
  cycleId: string;
  farmId: string;
  defaultRange?: TimeRange;
}

interface ChartReading {
  date: string;
  slot: string;
  ph: number | null;
  do: number | null;
  temperature: number | null;
  ammonia: number | null;
  salinity: number | null;
  transparency: number | null;
  depth: number | null;
  nitrite: number | null;
  alkalinity: number | null;
  alkalinity_2?: number | null;
  calcium?: number | null;
  magnesium?: number | null;
  transparency_clear_to_bottom?: boolean | null;
  [key: string]: string | number | boolean | null | undefined;
}

interface ChartDataResponse {
  readings: ChartReading[];
  range: string;
  start_date: string;
  end_date: string;
  checks?: ChartCheck[];
}

interface ParameterConfig {
  key: string;
  label: string;
  unit: string;
  thresholds?: { warning?: number | [number, number]; critical?: number | [number, number] };
}

const ALL_PARAMETERS: ParameterConfig[] = [
  { key: 'ph', label: 'pH', unit: '', thresholds: { warning: [7.6, 8.3], critical: [7.5, 8.4] } },
  { key: 'do', label: 'Dissolved O₂', unit: ' mg/L', thresholds: { warning: 5.0, critical: 4.0 } },
  { key: 'temperature', label: 'Temperature', unit: '°C', thresholds: { warning: [28, 31], critical: [25, 33] } },
  { key: 'ammonia', label: 'Ammonia', unit: ' ppm', thresholds: { warning: 0.5, critical: 1.0 } },
  { key: 'salinity', label: 'Salinity', unit: ' ppt', thresholds: { warning: [20, 25], critical: [15, 30] } },
  { key: 'transparency', label: 'Transparency', unit: ' cm' },
  { key: 'nitrite', label: 'Nitrite', unit: ' ppm' },
  { key: 'depth', label: 'Depth', unit: ' m' },
  { key: 'alkalinity', label: 'Alkalinity', unit: ' ppm' },
  { key: 'alkalinity_2', label: 'Alkalinity (other number)', unit: ' ppm' },
  { key: 'calcium', label: 'Calcium', unit: ' ppm' },
  { key: 'magnesium', label: 'Magnesium', unit: ' ppm' },
];

const DEFAULT_PANELS = ['ph', 'do', 'temperature', 'ammonia'];

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'cycle', label: 'Full Cycle' },
];

export function WaterQualityGrid({ cycleId, farmId, defaultRange = '7d' }: WaterQualityGridProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>(defaultRange);
  const [panels, setPanels] = useState<string[]>(DEFAULT_PANELS);

  const { data, isLoading, error } = useQuery({
    queryKey: ['water-readings-chart', cycleId, timeRange],
    queryFn: () =>
      api.get<{ data: ChartDataResponse }>(
        `/api/v1/farms/${farmId}/cycles/${cycleId}/readings/chart`,
        { params: { range: timeRange } }
      ),
  });

  const readings = data?.data?.readings || [];
  const checkRows = (data?.data?.checks ?? []).map(checkToChartRow) as unknown as ChartReading[];
  // Slot readings and ad-hoc checks on one time line, oldest first.
  const chartData = [...readings, ...checkRows]
    .map((r) => ({ r, at: `${r.date}T${r.kind === 'check' ? r.slot : slotClock(r.slot)}` }))
    .sort((a, b) => a.at.localeCompare(b.at))
    .map(({ r }) => r);

  const handlePanelChange = (index: number, newKey: string) => {
    setPanels(prev => {
      const next = [...prev];
      next[index] = newKey;
      return next;
    });
  };

  return (
    <Card>
      <Card.Header action={<TimeRangeSelector value={timeRange} onChange={setTimeRange} />}>
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-100 rounded-lg">
            <Droplets className="h-4 w-4 text-blue-600" />
          </div>
          Water Quality
        </div>
      </Card.Header>

      <Card.Body className="pt-0">
        {isLoading ? (
          <div className="h-[400px] flex items-center justify-center">
            <LoadingSpinner size={32} />
          </div>
        ) : error ? (
          <div className="h-[200px] flex items-center justify-center text-gray-400">
            Failed to load chart data
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 mt-4">
            {panels.map((paramKey, idx) => {
              const config = ALL_PARAMETERS.find(p => p.key === paramKey);
              if (!config) return null;

              return (
                <ChartPanel
                  key={`${paramKey}-${idx}`}
                  config={config}
                  data={chartData}
                  readings={readings}
                  checkRows={checkRows}
                  allParameters={ALL_PARAMETERS}
                  onParameterChange={(newKey) => handlePanelChange(idx, newKey)}
                />
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function ChartPanel({
  config,
  data,
  readings,
  checkRows,
  allParameters,
  onParameterChange,
}: {
  config: ParameterConfig;
  data: ChartReading[];
  readings: ChartReading[];
  checkRows: ChartReading[];
  allParameters: ParameterConfig[];
  onParameterChange: (key: string) => void;
}) {
  return (
    <div className="border border-gray-100 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <select
          value={config.key}
          onChange={(e) => onParameterChange(e.target.value)}
          className="text-sm font-medium text-gray-700 bg-transparent border-none p-0 focus:ring-0 cursor-pointer"
        >
          {allParameters.map(p => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </div>

      {config.key === 'ph' ? (
        <>
          <PHBarChart data={readings} height={280} />
          {checkRows.some((c) => c.ph !== null && c.ph !== undefined) && (
            <div className="mt-3 border-t border-gray-100 pt-2">
              <p className="mb-1 text-xs font-medium text-gray-500">Pre-stocking water checks</p>
              <LineChart data={checkRows} dataKey="ph" label="pH" unit="" height={160} />
            </div>
          )}
        </>
      ) : (
        <LineChart
          data={data}
          dataKey={config.key}
          label={config.label}
          unit={config.unit}
          thresholds={config.thresholds}
          height={280}
          clearToBottom={config.key === 'transparency' ? { flagKey: 'transparency_clear_to_bottom', depthKey: 'depth' } : undefined}
        />
      )}
    </div>
  );
}

function TimeRangeSelector({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (v: TimeRange) => void;
}) {
  return (
    <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
      {TIME_RANGES.map((range) => (
        <button
          key={range.value}
          onClick={() => onChange(range.value)}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-all duration-150',
            value === range.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          {range.label}
        </button>
      ))}
    </div>
  );
}
